import { LitElement, html, css } from 'lit';
import { fetchRatingParameters } from '../../../api/head-apis';

class RatingDialog extends LitElement {
  static properties = {
    ratingParameters: { type: Array },
    loading: { type: Boolean },
    error: { type: String },
    selectedRatings: { type: Object },
    reviewText: { type: String },
    token: { type: String },
    conversationId: { type: String },
    orderId: { type: String },
    rateType: { type: String }, // 'buyer' or 'dealer'
    onConfirm: { type: Function },
    onCancel: { type: Function },
    submitted: { type: Boolean }
  };

  constructor() {
    super();
    this.ratingParameters = [];
    this.loading = true;
    this.error = '';
    this.selectedRatings = {};
    this.reviewText = '';
    this.submitted = false;
  }

  static styles = css`
    :host {
      --popup-bg: var(--bg-primary-color, #fff);
      --popup-text: var(--text-primary-color, #18181b);
      --popup-option-bg: var(--bg-secondary-color, #f3f4f6);
      --popup-option-bg-selected: var(--base-color, #241172);
      --popup-option-text: var(--text-primary-color, #18181b);
      --popup-option-text-selected: var(--white, #fff);
      --popup-btn-bg: var(--base-color, #241172);
      --popup-btn-text: var(--white, #fff);
      --popup-btn-radius: 8px;
      --popup-btn-font: 16px;
      --popup-overlay-bg: var(--popup-overlay, rgba(0,0,0,0.25));
      --popup-error: var(--text-danger-color, #d32f2f);
      --popup-border-color: var(--border-color, #e1e5e9);
      --dialog-max-width: 500px;
      display: block;
      min-width: 340px;
      max-width: 95vw;
    }
    
    .dialog-wrapper {
      max-width: var(--dialog-max-width, 500px) !important;
    }

    .rating-dialog {
      padding-top: 10px;
      max-height: 80vh !important;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      max-width: 500px !important;
    }
      

    .rating-header {
      text-align: center;
      margin-bottom: 24px;
    }

    .rating-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--popup-text);
      margin: 0 0 8px 0;
    }

    .rating-subtitle {
      font-size: 14px;
      color: var(--text-secondary-color);
      margin: 0;
    }

    .loading-container {
      text-align: center;
      padding: 40px 20px;
      color:var(--text-secondary-color);
    }

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid var(--popup-option-bg);
      border-top: 4px solid var(--popup-btn-bg);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 16px;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .error-message {
      color: var(--popup-error);
      text-align: center;
      padding: 20px;
      font-size: 14px;
    }

    .rating-parameters {
      display: flex;
      flex-direction: column;
      gap: 10px;
      flex: 1;
      overflow-y: auto;
      scrollbar-width: thin;
      padding: 14px;
    }

    .rating-parameter {
      border-radius: 8px;
      padding: 12px;
      background: var(--popup-bg);
      min-width: 0;
    }

    .parameter-title {
      font-size: 16px;
      font-weight: 600;
      color: var(--popup-text);
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .stars-container {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: nowrap;
      min-width: 0;
      justify-content: center;
    }

    .star {
      cursor: pointer;
      font-size: 30px;
      color: #d1d5db;
      transition: color 0.2s;
      flex-shrink: 0;
    }

    .star:hover,
    .star.active {
      color: var(--popup-btn-bg);
    }

    .star.filled {
      color: var(--popup-btn-bg);
    }

    .rating-value {
      font-size: 14px;
      color: var(--text-secondary-color);
      margin-left: 12px;
    }

    .dialog-actions {
      display: flex;
      gap: 12px;
      margin-top: 24px;
      justify-content: flex-end;
      flex-shrink: 0;
    }

    .btn {
      padding: 10px 20px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      border: none;
      transition: all 0.2s;
    }

    .btn-cancel {
      background: transparent;
      color: var(--text-secondary-color);
      border: 1px solid var(--popup-border-color);
    }

    .btn-cancel:hover {
      background: var(--popup-option-bg);
    }

    .btn-submit {
      background: var(--popup-btn-bg);
      color: var(--popup-btn-text);
    }

    .btn-submit:hover {
      background: var(--popup-option-bg-selected);
    }

    .btn-submit:disabled {
      background: var(--text-secondary-color);
      cursor: not-allowed;
    }

    .no-ratings-message {
      text-align: center;
      color: var(--text-secondary-color);
      padding: 40px 20px;
      font-size: 14px;
    }

    .review-textarea {
      border: 1px solid var(--popup-border-color);
      border-radius: 8px;
      padding: 12px;
      font-size: 14px;
      font-family: inherit;
      resize: vertical;
      min-height: 80px;
      outline: none;
      transition: border-color 0.2s;
      background: var(--popup-bg);
      color: var(--popup-text);
      width: 400px;
    }

    .review-textarea:focus {
      border-color: var(--popup-btn-bg);
    }

    .review-textarea::placeholder {
      color: var(--text-secondary-color);
    }

    .review-section {
      margin-top: 20px;
    }

    .review-label {
      font-size: 16px;
      font-weight: 600;
      color: var(--popup-text);
      margin-bottom: 8px;
      display: block;
    }

    .success-message {
      color: var(--text-success-color);
      text-align: center;
      padding: 20px;
      font-size: 14px;
      font-weight: 600;
    }

    .success-container {
      text-align: center;
      padding: 40px 20px;
    }

    .success-icon {
      font-size: 48px;
      color: var(--text-success-color);
      margin-bottom: 16px;
    }

    @media (max-width: 768px) {
      .review-textarea {
        width: 100%;
      }
        .dialog-wrapper {
           width: 100%;
        }
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadRatingParameters();
  }

  async loadRatingParameters() {
    try {
      this.loading = true;
      this.error = '';
      this.requestUpdate();

      const response = await fetchRatingParameters(this.token);
      
      let allParameters = [];
      if (response.data && response.data.ratingParameters && Array.isArray(response.data.ratingParameters)) {
        allParameters = response.data.ratingParameters;
      } else if (response.data && Array.isArray(response.data)) {
        // Fallback for direct array response
        allParameters = response.data;
      } else {
        this.error = 'No rating parameters found';
        this.loading = false;
        this.requestUpdate();
        return;
      }

      // Filter parameters based on rateType
      const targetLinkedWith = this.rateType === 'buyer' ? 'BUYER' : 'SELLER';
      this.ratingParameters = allParameters.filter(param => param.linkedWith === targetLinkedWith);
      
      // Initialize selected ratings using _id as the key
      this.ratingParameters.forEach(param => {
        this.selectedRatings[param._id || param.id] = 0;
      });

      if (this.ratingParameters.length === 0) {
        this.error = `No rating parameters found for ${this.rateType === 'buyer' ? 'buyer' : 'seller'} rating.`;
      }
    } catch (error) {
      console.error('Error loading rating parameters:', error);
      this.error = 'Failed to load rating parameters. Please try again.';
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  handleStarClick(parameterId, rating) {
    this.selectedRatings[parameterId] = rating;
    this.requestUpdate();
  }

  handleReviewChange(e) {
    this.reviewText = e.target.value;
    this.requestUpdate();
  }

  async handleSubmit() {
    // Check if any parameter has been rated AND review text is provided
    const hasAnyRating = this.ratingParameters.some(param => this.selectedRatings[param._id || param.id] > 0);
    const hasReviewText = this.reviewText.trim().length > 0;
    
    if (!hasAnyRating) {
      this.error = 'Please rate at least one parameter before submitting.';
      this.requestUpdate();
      return;
    }
    
    if (!hasReviewText) {
      this.error = 'Please add a review before submitting.';
      this.requestUpdate();
      return;
    }

    // Prepare the payload
    const ratingsArray = Object.entries(this.selectedRatings)
      .filter(([_, rating]) => rating > 0) // Only include ratings > 0
      .map(([parameterId, rating]) => ({
        parameterId: parameterId,
        rating: rating
      }));

    const payload = {
      orderId: this.orderId,
      linkedWith: this.rateType === 'buyer' ? 'SELLER' : 'BUYER',
      rating: ratingsArray,
      review: this.reviewText.trim()
    };

    // Close dialog immediately and let parent handle the API call
    if (this.onConfirm) {
      this.onConfirm(payload);
    }
  }

  handleCancel() {
    if (this.onCancel) {
      this.onCancel();
    }
  }

  renderStar(parameterId, starNumber, currentRating) {
    const isFilled = starNumber <= currentRating;
    const isActive = starNumber <= currentRating;
    
    return html`
      <span 
        class="star ${isFilled ? 'filled' : ''} ${isActive ? 'active' : ''}"
        @click=${() => this.handleStarClick(parameterId, starNumber)}
        @mouseenter=${() => this.handleStarHover(parameterId, starNumber)}
        @mouseleave=${() => this.handleStarLeave(parameterId)}
      >
        ★
      </span>
    `;
  }

  handleStarHover(parameterId, starNumber) {
    // Add hover effect logic if needed
  }

  handleStarLeave(parameterId) {
    // Remove hover effect logic if needed
  }

  render() {
    if (this.submitted) {
      return html`
        <div class="rating-dialog">
          <div class="success-container">
            <div class="success-icon">✓</div>
            <h3 class="rating-title">Rating Submitted Successfully!</h3>
            <p class="rating-subtitle">Thank you for your feedback</p>
          </div>
          <div class="dialog-actions">
            <button class="btn btn-submit" @click=${this.handleCancel}>
              Close
            </button>
          </div>
        </div>
      `;
    }

    if (this.loading) {
      return html`
        <div class="rating-dialog">
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading rating parameters...</p>
          </div>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="rating-dialog">
          <div class="error-message">
            ${this.error}
          </div>
          <div class="dialog-actions">
            <button class="btn btn-cancel" @click=${this.handleCancel}>
              Close
            </button>
          </div>
        </div>
      `;
    }

    if (this.ratingParameters.length === 0) {
      return html`
        <div class="rating-dialog">
          <div class="no-ratings-message">
            No rating parameters available.
          </div>
          <div class="dialog-actions">
            <button class="btn btn-cancel" @click=${this.handleCancel}>
              Close
            </button>
          </div>
        </div>
      `;
    }

    const rateTypeText = this.rateType === 'buyer' ? 'Buyer' : 'Dealer';
    const hasAnyRating = this.ratingParameters.some(param => this.selectedRatings[param._id || param.id] > 0);
    const hasReviewText = this.reviewText.trim().length > 0;
    const canSubmit = hasAnyRating && hasReviewText;

    return html`
      <div class="rating-dialog">
        <div class="rating-header">
          <h3 class="rating-title">Rate ${rateTypeText}</h3>
          <p class="rating-subtitle">Your feedback helps improve our marketplace community.<br>Please rate your experience.</p>
        </div>

        <div class="rating-parameters">
          ${this.ratingParameters.map(parameter => html`
            <div class="rating-parameter">
              <div class="parameter-title">${parameter.title}</div>
              <div class="stars-container">
                ${[1, 2, 3, 4, 5]?.map(star => 
                  this.renderStar(parameter._id || parameter.id, star, this.selectedRatings[parameter._id || parameter.id])
                )}
                <span class="rating-value">
                  ${this.selectedRatings[parameter._id || parameter.id] > 0 ? `${this.selectedRatings[parameter._id || parameter.id]}/5` : ''}
                </span>
              </div>
            </div>
          `)}

            <div class="review-section">
          <label class="review-label">Review *</label>
          <textarea
            class="review-textarea"
            placeholder="Write your review here... (Required)"
            .value=${this.reviewText}
            @input=${this.handleReviewChange}
            rows="4"
            required
          ></textarea>
        </div>
        </div>

      

        ${this.error ? html`
          <div class="error-message">
            ${this.error}
          </div>
        ` : ''}

        <div class="dialog-actions">
          <button class="btn btn-cancel" @click=${this.handleCancel}>
            Cancel
          </button>
          <button 
            class="btn btn-submit" 
            ?disabled=${!canSubmit}
            @click=${this.handleSubmit}
          >
            Submit Rating
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('rating-dialog', RatingDialog); 